import { t } from '@lingui/core/macro';
import { UserGroupDataQualityField } from '@navio/nsdk';
import { NoddiButton } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { useAuthContext, useIsGroupAdmin, UserGroupDataQualityBanner } from 'noddi-web';
import { useNavigate } from 'react-router-dom';
import { useUserGroupSelection } from '../../hooks/useUserGroupSelection';
import { isIssueRelevantToCustomers } from './customerDataQualityFixes';
import { getUserGroupDataQualityTranslations } from './translations';
import { useCustomerDataQualityGate } from './useCustomerDataQualityCheckout';

/**
 * Banner for the provider's report. Renders the provider's record rather than
 * fetching again, so a page shows one report no matter how many surfaces read it.
 */
export const UserGroupDataQualityPromptCustomer = () => {
  const { userData, currentUserGroupId } = useAuthContext();
  const gate = useCustomerDataQualityGate();
  const navigate = useNavigate();
  const { selectUserGroup } = useUserGroupSelection();
  const primaryContactMissing =
    gate.record?.issues.some((issue) => issue.field === UserGroupDataQualityField.PRIMARY_CONTACT) ?? false;
  // The members page is admin-only. Ask only when this report can be fixed there.
  const { isGroupAdmin } = useIsGroupAdmin(
    primaryContactMissing ? gate.record?.userGroupId : undefined,
    primaryContactMissing ? userData?.user.id : undefined
  );
  const canSetPrimaryContact = primaryContactMissing && isGroupAdmin === true;

  const openPrimaryContact = () => {
    const userGroupId = gate.record?.userGroupId;
    if (userGroupId != null && userGroupId !== currentUserGroupId) {
      selectUserGroup(userGroupId, { keepBookingDrafts: true });
    }
    void navigate(`${customerRoutes.organizationMembers.getPath()}#primary-contact`);
  };

  if (!gate.showNotices) {
    return null;
  }

  return (
    <UserGroupDataQualityBanner
      record={gate.record}
      hasError={gate.hasError}
      showMembers={gate.showMembers}
      // The signed-in user's own issues are already on the group banner above.
      excludeMemberUserId={userData?.user.id}
      translations={getUserGroupDataQualityTranslations()}
      isVisibleIssue={isIssueRelevantToCustomers}
      renderAction={
        gate.canFix || canSetPrimaryContact
          ? () => (
              <>
                {canSetPrimaryContact && (
                  <NoddiButton size='small' variant='primary' onClick={openPrimaryContact}>
                    {t`Set primary contact`}
                  </NoddiButton>
                )}
                {gate.canFix && (
                  <NoddiButton size='small' variant='primary' onClick={gate.openFix}>
                    {t`Update details`}
                  </NoddiButton>
                )}
              </>
            )
          : undefined
      }
      renderMemberAction={
        gate.showMembers
          ? () => (
              <NoddiButton
                size='small'
                variant='primary'
                // oxlint-disable-next-line typescript/no-misused-promises -- navigate() only tracks lazy-route loading; React discards button-handler returns
                onClick={() => navigate(customerRoutes.organizationMembers.getPath())}
              >
                {t`Review members`}
              </NoddiButton>
            )
          : undefined
      }
    />
  );
};
